import { Component, Log, Modal } from '@WidgetWorks';
import UploadButton from '../Shared/UploadButton';
import CropperButton from '../../Mixins/CropperButton';

export default class UploadRecommendationWindow extends Component {
    constructor(appConfig) {
        super(appConfig);

        // API Filter Configurations
        this.apiFilter = this.productPageInfo.retailerConfig?.filter_config?.recommendation;

        // An upload button.
        this.uploadButton = new UploadButton(appConfig.childApps.UploadButton, this.apiFilter);
        this.uploadButton.displayApp = this;
        this.uploadButton.parentName = 'StyleSnap';

        // First interaction point
        this.interacted = false;

        // Will be needed for checkout script.
        this.clickedProducts = this.loadClickedProducts();

        // Default gender (all for uploads)
        this.selectedGender = '0';
        // Cropper
        this.cropperButton = null;

        // Mode
        // Used by filters to know what request to repeat.
        // Sorta manages state.
        this.mode = {
            current: 'upload',
            previous: 'upload',
            default: 'upload',
            func: {
                recommendation: null,
                upload: null, // will exist as this.uploadButton.uploadPicture
                cropper: null, // will exist as this.cropperButton.uploadPicture
            },
            params: {
                recommendation: [],
                upload: [],
                cropper: [],
            },
            instance: {
                recommendation: null,
                upload: null,
                cropper: null,
            },
        };

        // Old values
        this.oldRecommendations = null;
        this.oldCurrentProduct = null;
        this.oldSelectedGender = null;

        // Designed to create an error. This
        // app is not supposed to have multiple points.
        this.elementCopies = null;

        // Analytics Cache
        this.seenItems = {};
    }

    overrideHook() {}

    preRenderHook() {}

    beforeMountHook() {
        // Handlers
        this.root = this.element.shadowRoot.querySelector('section');

        // Add in the logo
        if (this.resources.logo) {
            const logoPoint = this.root.querySelector('#header-customer-logo');
            logoPoint.src = this.resources.logo;
        }

        // Add in the filters / sort section
        if (this.appConfig.sortDropdown) {
            this.insertFilters();
        }

        // Add in the upload button if exists
        if (this.uploadButton) {
            this.insertUploadButton();
        }

        // Add the listeners for the langs selector
        this.addEventListenerForDropDown();
        this.updateLanguageDropdown();

        // Assuming background is a child element of this.root
        // And if the close button id is present too
        // const background = this.root.querySelector('.background');
        const closeElements = this.root.querySelectorAll('[close]');

        closeElements.forEach((elm) => {
            elm.addEventListener('click', (event) => {
                event.preventDefault();
                // Check if the click target is the background (not the floating window)
                // if (event.target === elm && event.target.id !== 'image_upload_button') {
                // Close the window by calling the toggleDisplay method of the class
                this.toggleDisplay();
                // }
            });
        });
        // Setup listners for the gender filters
        this.setupGenderFilter();
        this.setupCropperButton();

        // At this point, should we pre-render the page?
        // As opposed to rendering the page once the user has clicked?
        // The config tells us.
        if (this.appConfig.pre_render) {
            this.recommendationsController(this.productPageInfo.productId);
        }
    }

    postRenderHook() {
        // Set the functions modes
        this.mode.func.upload = this.uploadButton.handleFilterChange;
        this.mode.func.cropper = this.cropperButton.handleFilterChange;

        // Grab the instance
        this.mode.instance.upload = this.uploadButton;
        this.mode.instance.cropper = this.cropperButton;

        return true;
    }

    setupGenderFilter() {
        const genderDropdowns = this.root.querySelectorAll('[gender-filter-dropdown]');

        for (const dropdown of genderDropdowns) {
            // Set the default inherit value
            dropdown.value = this.selectedGender;

            dropdown.addEventListener('change', ({ target }) => {
                // Do nothing if selecting the already selected gender
                if (this.selectedGender === target.value) return;

                // Update internal state
                this.selectedGender = target.value;

                // Ensure every other dropdown is updated
                genderDropdowns.forEach((element) => {
                    const x = element;
                    x.value = this.selectedGender;
                });

                // Trigger the recommendations controller again
                // !! Use the last method of upload / recommendation
                // !! Looks wild right? but it's a pretty cool way of calling the last method.
                const lastUsedMethod = this.mode.func[this.mode.current];
                const lastUsedParams = this.mode.params[this.mode.current];
                lastUsedMethod.call(this.mode.instance[this.mode.current], ...lastUsedParams);
                // this.mode.func[this.mode.current](...this.mode.params[this.mode.current]);
                // this.recommendationsController(this.productPageInfo.productId);
            });
        }
    }

    insertUploadButton() {
        // Upload button is a child app with the
        // the rest of the apps details.

        // Initialise the app and get a handle on the
        // root element.
        const uploadButtonPromise = this.uploadButton.init();
        this.uploadButtonElement = this.uploadButton.element;

        Promise.allSettled([uploadButtonPromise]).then((results) => {
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    const error = new Error(result.reason.message);
                    error.stack = result.reason.stack;
                    this.handleError(error);
                }
            });
        });

        // As this is a child app, the parent here is responsible for mounting
        // the application.
        this.uploadButton.mountElements(this.root);
    }

    updateDataView(currentProduct, recommendations, gender) {
        try {
            // sync filters
            this.selectedGender = gender;
            this.syncGenderFilter();

            this.renderMainProduct(currentProduct, '#main-product');
            this.renderRecommendations(recommendations, '#grid');
        } catch (error) {
            this.handleError(error);
        }
    }

    restoreOldDefaultView() {
        this.recommendations = this.oldRecommendations;
        this.currentProduct = this.oldCurrentProduct;
        this.selectedGender = this.oldSelectedGender;

        // Delete the old ones.
        // this.oldRecommendations = null;
        // this.oldCurrentProduct = null;
        this.syncGenderFilter();
        this.renderMainProduct(this.currentProduct, '#main-product');
        this.renderRecommendations(this.recommendations, '#grid');
    }

    cropperRestart() {
        // Restart the cropper
        this.cropperButton.restart();
    }

    uploadResponseAnalytics() {
        this.analytics.registerEvent('upload_response', 1, 'StyleSnap');
        // this.analytics.registerEvent('upload_seen', 1, 'StyleSnap');
    }

    renderUploadRecommendations(recommendations, usersPhoto, userTitle = null) {
        this.syncGenderFilter();

        // Restart the cropper
        this.cropperRestart();

        // Store the old ones away.
        // this.oldRecommendations = [...this.recommendations];
        // this.oldCurrentProduct = { ...this.currentProduct };

        // Set title to the expected language.
        // !! Fix this fuckery, lang should not be done like this
        const title = this.language.recommendation_window.uploaded_picture;
        const newTitle = userTitle ?? title;
        const newProduct = { title: newTitle };
        newProduct.url = URL.createObjectURL(usersPhoto);

        // old values
        this.oldRecommendations = this.recommendations;
        this.oldCurrentProduct = this.currentProduct;
        this.oldSelectedGender = this.selectedGender;

        // Set the new values.
        this.currentProduct = newProduct;
        this.recommendations = recommendations;
        // this.selectedGender = this.selectedGender;

        this.renderMainProduct(this.currentProduct, '#main-product');
        this.renderRecommendations(this.recommendations, '#grid');

        this.hideElement('#loader', 1000);
    }

    addEventListenerForDropDown() {
        const languageDropdown = this.root.querySelector('[language-dropdown-button]');
        const dropdownContent = this.root.querySelector('[language-dropdown]');

        // Run language change event
        const languageElements = this.root.querySelectorAll('[language]');
        languageElements.forEach((lang) => {
            lang.addEventListener('click', (event) => {
                const { target } = event;
                const langCode = target.getAttribute('language');
                this.changeLanguage(langCode);
            });
        });

        // Auto open the widget if button is clicked
        languageDropdown.addEventListener('click', () => {
            this.openLanguageDropdown();
        });

        // auto closer
        this.root.addEventListener('click', (event) => {
            const { target } = event;
            if (target === languageDropdown || target === dropdownContent) return;
            if (!dropdownContent.contains(target) && !languageDropdown.contains(target)) {
                this.closeLanguageDropdown();
            }
        });
    }

    updateLanguageDropdown() {
        const languageDropdown = this.root.querySelector('[language-dropdown-button]');
        const langCode = this.productPageInfo.languageCode;
        languageDropdown.setAttribute('setLang', langCode);

        //  Automatically hide the language from the dropdown
        const allDefaultLangs = this.root.querySelectorAll(`[language=${langCode}]`);
        allDefaultLangs.forEach((lang) => {
            const langElement = lang;
            langElement.style.display = 'none';
        });
    }

    openLanguageDropdown() {
        const dropDown = this.root.querySelector('[language-dropdown]');
        dropDown.style.display = 'flex';
    }

    closeLanguageDropdown() {
        const dropDown = this.root.querySelector('[language-dropdown]');
        dropDown.style.display = 'none';
    }

    async changeLanguage(lang) {
        // Change the language of the app.
        this.root.setAttribute('language', lang);

        // Restart the language dropdown
        this.root.querySelectorAll('[language]').forEach((langElement) => {
            const element = langElement;
            element.style.display = 'flex';
        });

        // hide the new language from the dropdown
        const allLangs = this.root.querySelectorAll(`[language=${lang}]`);
        allLangs.forEach((langElement) => {
            const element = langElement;
            element.style.display = 'none';
        });

        // Craft url
        const url = `./content/langs/${lang}.json`;

        // Get the new language
        const newLang = await this.appFactory.loadAllPromises([url]);
        this.language = JSON.parse(newLang);

        this.insertLanguages(this.language);
        this.uploadButton.updateLanguagesForAllCopies(this.language);
        return true;
    }

    errorPage(header = 'Error Occurred', message = 'An unknown error has occurred, please try again later') {
        this.modal = new Modal(this.root, header, message, this.language.upload_button_app.modal);
        this.modal.display();
    }

    firstInteractionHook() {
        if (this.interacted) return false;
        this.interacted = true;
        return true;
    }

    // !! create a singleton app installer
    async mountElement() {
        let counter = 0;

        if (this.mountPoints.length > 1) {
            console.log(...Log.w(`${this.constructor.name}`, 'WARN', 'App is singleton, only one point will be mounted'));
        }

        try {
            const point = this.mountPoints[0];
            const mountPoint = this.mountPoints[0].mount_points[0];
            await this.waitForElementToExist(mountPoint);
            const targetElement = document.querySelector(mountPoint);
            if (targetElement) {
                targetElement.appendChild(this.element);
            }

            // Insert attributes into the new elements
            this.root.setAttribute('mountId', point.mount_id);
            point.attributes.forEach((attr) => {
                this.root.setAttribute(attr, '');
            });

            this.root.setAttribute('mountPointIndex', counter);
            this.root.setAttribute('language', this.productPageInfo.languageCode);

            counter += 1;
        } catch (error) {
            console.warn(`Could not mount this element correctly ${error}`);
            console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `Could not mount this element: ${error}`));
        }
        console.log(...Log.w(`${this.constructor.name}`, 'LOG', `Mounted ${counter} elements (Max 1)`));
        return true;
    }

    async init() {
        console.log(...Log.w(`${this.constructor.name}`, 'LOG', 'Initialising...'));

        // This hook will run the custom function defined in the override itself.
        this.overrideHook();

        // Before rendering, do these things.
        this.preRenderHook();

        // Insert CSS into virtual element
        this.insertCSS(this.styles);

        // Insert HTML into virtual element
        this.insertHTML(this.markup);

        // Update HTML with Languages into the virtual element
        this.insertLanguages(this.language);

        // Run the pre mounted hook.
        this.beforeMountHook();

        // Once the mount point is available, mount it.
        // This is a singular mount, not copies.
        await this.mountElement();

        // Now mounted, run the post hooks
        this.postRenderHook();

        console.log(...Log.w(`${this.constructor.name}`, 'LOG', 'Initialised'));
    }

    // async recommendationsController(productId) {
    //     this.showElement('#loader');
    //     this.recommendations = await this.getRecommendationsApi(productId);
    //     this.currentProduct = this.recommendations.pop();
    //     this.renderMainProduct(this.currentProduct, '#main-product');
    //     this.renderRecommendations(this.recommendations, '#grid');

    //     // Hook up the products viewed event:
    //     this.productObserver();
    //     this.hideElement('#loader', 1000);
    // }

    insertFilters() {
        // Default Sorting value.
        this.sortBy = 'similar';

        // Create handlers
        const filterContainers = this.root.querySelectorAll('[sortBy]');

        // Display it.
        // filterContainer.style.display = "inherit";

        for (const filterContainer of filterContainers) {
            // Default sorts are price and similarity (low + high on price).
            const dropdown = filterContainer.querySelector('#sortByDropdown');
            // const lang = this.language.recommendation_window.dropdown;
            // dropdown.innerHTML += `
            // <option value="similar" default similar>${lang.similar}</option>
            // <option value="priceHigh" price_high>${lang.price_high}</option>
            // <option value="priceLow" price_low>${lang.price_low}</option>`;

            // Create the event hook
            dropdown.addEventListener('change', ({ target }) => {
                // Sort by the new value and re-render
                const contentToShow = this.sortRecommendations(target.value);
                this.renderRecommendations(this.recommendations, '#grid', contentToShow);
            });
        }

        return true;
    }

    sortRecommendations(sortBy) {
        switch (sortBy) {
        case 'similar':
            this.recommendations.sort((a, b) => a.qdrant.score - b.qdrant.score);
            return 'title';
        case 'priceHigh':
            this.recommendations.sort((a, b) => b.price - a.price);
            return 'full_price';
        case 'priceLow':
            this.recommendations.sort((a, b) => a.price - b.price);
            return 'full_price';
        default:
            // Default to no sorting if sortBy is invalid
            break;
        }

        return 'title';
    }

    // !! Place into a mixin.
    // !! so logic can be shared among apps.
    // async getRecommendationsApi(productId) {
    //     try {
    //         const requestData = {
    //             prime_id: this.productPageInfo.retailerId,
    //             product_id: productId,
    //             retailer_shopify_domain: this.productPageInfo.retailerId,
    //             device: this.productPageInfo.userDevice,
    //             development: 'false',
    //             user_continent: 'UK',
    //             filterOnA0: 'false',
    //             cache: this.productPageInfo.useCache,
    //             category: '[]',
    //             gender: '',
    //             colors: '[]',
    //         };

    //         const formData = new FormData();

    //         for (const key in requestData) {
    //             if (requestData.hasOwnProperty(key)) {
    //                 formData.append(key, requestData[key]);
    //             }
    //         }

    //         const response = await fetch(this.productPageInfo.retailerConfig.urls.apis.recommendation_api, {
    //             method: 'POST',
    //             body: formData,
    //         });

    //         if (!response.ok) {
    //             console.error(`Error: ${response.status} ${response.statusText}`);
    //             console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `Error: ${response.status} ${response.statusText}`));
    //             return false;
    //         }

    //         const json = await response.json();
    //         const recommendations = json.response.response_object.new_feed;
    //         return recommendations;
    //     } catch (error) {
    //         console.error(error);
    //         console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `Could not get recommendations for ${productId}`));
    //         return false;
    //     }
    // }
    setupCropperButton() {
        const buttonContainer = this.root.querySelector('[cropper-button-container]');
        const productImage = this.root.querySelector('#main-product-image');
        this.cropperButton = new CropperButton(buttonContainer, productImage, this);
        this.cropperButton.init();
        // this.root.querySelector('#cropper-button').addEventListener('click', this.initialiseCropper.bind(this));
        // this.root.querySelector('#finalCrop').addEventListener('click', this.cropImage.bind(this));
    }

    /* The above code is defining a function called "renderMainProduct" in JavaScript. */
    renderMainProduct(currentProduct, productPoint) {
        // render current product.

        // Get handlers
        const container = this.root.querySelector(`${productPoint}`);

        // Update the main product image
        container.querySelector('#main-product-image').src = currentProduct.url;

        // Update the main product title
        container.querySelector('#main-product-title').textContent = currentProduct.title;

        // * 0.6.0 New Cropper Mechanism
        // Register Cropper
        // when clicked run the cropper
        // container.querySelector('#cropperText').addEventListener('click', this.initialiseCropper.bind(this));
        // container.querySelector('#finalCrop').addEventListener('click', this.cropImage.bind(this));

        return true;
    }

    syncGenderFilter() {
        const genderDropdowns = this.root.querySelectorAll('[gender-filter-dropdown]');
        for (const dropdown of genderDropdowns) {
            dropdown.value = this.selectedGender;
        }
    }

    // TODO: CREATE DEFAULT VALUES
    renderRecommendations(recommendations, insertionPoint, titleToShow = 'title') {
        // Update all the filters to match incoming ones
        // this.syncGenderFilter();
        // Get handlers
        const container = this.root.querySelector(`${insertionPoint}`);

        // Clear any current HTML
        container.innerHTML = '';

        // Render the recommendations
        for (const product of recommendations) {
            container.innerHTML += `
			<div class="card-content center-flex clickMe" productId="${product.id}" card>
				<div class="card-image-container center-flex">
					<img src="${product.url}" class="card-image" width="308" height="400">
				</div>
				<div class="card-text-container flex-row">
					<p class="text">${product[titleToShow]}</p>
				</div>
			</div>`;
        }

        // Register Error handling (If config says so)
        if (this.appConfig.hideNoPicture) {
            this.registerImageErrorHandlers(recommendations);
        }

        // Also want it so that when clicked, they redirect
        this.registerProductRedirects(recommendations);

        // Watch any clicks.
        this.productObserver();

        return true;
    }

    registerImageErrorHandlers(recommendations) {
        recommendations.forEach((product) => {
            const x = this.root.querySelector(`[productId="${product.id}"] img`);
            x.addEventListener('error', (event) => {
                event.preventDefault();
                this.root.querySelector(`[productId="${product.id}"]`).style.display = 'none';
                console.warn('PRIME AI FCBP | We could not load this image. Feed is not in sync');
            });
        });
    }

    registerProductRedirects(recommendations) {
        recommendations.forEach((product) => {
            const x = this.root.querySelector(`[productId="${product.id}"]`);
            x.addEventListener('click', (event) => {
                event.preventDefault();
                this.openProduct(product);
            });
        });
    }

    loadClickedProducts() {
        // Read in `StyleSnapClickedProducts` from local storage if it exists, or create an empty array
        const clickedProductsJSON = localStorage.getItem('StyleSnapClickedProducts');
        if (clickedProductsJSON) {
            const clicked = JSON.parse(clickedProductsJSON);
            console.log(...Log.w(`${this.constructor.name}`, 'LOG', `Clicked Items in storage count: ${clicked}`));
            return clicked;
        }
        return [];
    }

    saveClickedProducts() {
        // Save the clicked products array to local storage
        localStorage.setItem('StyleSnapClickedProducts', JSON.stringify(this.clickedProducts));
    }

    placeProductIntoLocalStorage(productDetails) {
        // Check if the product has already been clicked
        const existingProduct = this.clickedProducts.find((clickedProduct) => clickedProduct.product.productDetails.productid === productDetails.productid);

        if (existingProduct) {
            console.log(...Log.w(`${this.constructor.name}`, 'LOG', 'Adding existing product to storage (counter & origin update)'));
            // If the product has already been clicked, increase the click counter
            existingProduct.product.pageDetails.counter += 1;
            // Add the origin to the array
            existingProduct.product.pageDetails.origin.push(this.productPageInfo.productId);
        } else {
            console.log(...Log.w(`${this.constructor.name}`, 'LOG', 'Adding new product to storage.'));
            // If the product is clicked for the first time, create a new entry
            this.clickedProducts.push({
                product: {
                    productDetails,
                    pageDetails: {
                        counter: 1,
                        origin: [this.productPageInfo.productId],
                        app: 'UploadButtonFromStyleSnap',
                    },
                },
            });
        }

        // Save the updated clicked products array to local storage
        this.saveClickedProducts();
    }

    openProduct(productDetails) {
        // Super tricky one here, as technically it's all an upload.
        // but we want hte inital upload to only be a click.
        // while a further upload is considered a upload_click
        // so we check the internal buttons upload state.

        if (this.uploadButton.elementCopies[0].shadowRoot.querySelector('[title]').style.display !== 'none') {
            // Something to do with Fetch and analytics
            this.analytics.registerEvent('click', 1, 'StyleSnap');
        } else {
            this.analytics.registerEvent('upload_click', 1, 'StyleSnap');
        }

        this.analytics.registerProductClickEvent(productDetails.productid, 'StyleSnap', productDetails.id);

        // Place into localstorage.
        this.placeProductIntoLocalStorage(productDetails);

        // Redirect
        window.open(productDetails.link2page, '_blank');
    }

    showElement(elementId) {
        const element = this.root.querySelector(elementId);
        if (element) {
            element.style.display = 'block'; // Replace with the desired display value
        }
    }

    hideElement(elementId, delay = 0) {
        const element = this.root.querySelector(elementId);

        if (element) {
            setTimeout(() => {
                element.style.display = 'none';
            }, delay);
        }
    }

    productObserver() {
        const elements = this.root.querySelectorAll('[productid]');

        // Initialize variables to store the product views and a timeout ID
        const productViews = [];
        let timeoutId;

        // Function to register the event
        const registerEvent = (count) => {
            // !! UPDATE THIS FOR NEW APP
            this.analytics.registerEvent('sliders_items_displayed', count, 'StyleSnap');
        };

        // Function to handle the product view
        const handleProductView = () => {
            // Clear the previous timeout (if any)
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // Set a new timeout of 5 seconds
            timeoutId = setTimeout(() => {
            // Execute the event registration with the count of product views
                registerEvent(productViews.length);

                // Clear the productViews array
                productViews.length = 0;
            }, 5000); // 5000 milliseconds = 5 seconds
        };

        for (const element of elements) {
            // Options for the Intersection Observer
            const options = {
                root: null, // Use the viewport as the root
                rootMargin: '0px', // No additional margin around the viewport
                threshold: 0.5, // Trigger when 50% of the element is in view
            };

            // Create an Intersection Observer
            const productObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (this.seenItems[entry.target.getAttribute('productid')]) return;
                        // The element is in view
                        // Simulate a product view by pushing the product into the productViews array
                        productViews.push(entry.target);

                        // Add to the seen items cache
                        this.seenItems[entry.target.getAttribute('productid')] = true;

                        // Trigger the function to handle the view and reset the timeout
                        handleProductView();

                        // Stop observing after the action is executed (optional)
                        observer.unobserve(element);
                    }
                });
            }, options);

            // Start observing the target element
            productObserver.observe(element);
        }
    }

    toggleLoading() {
        const loader = this.root.querySelector('#loader');
        if (loader.style.display === 'none') {
            loader.style.display = 'block'; // Replace with the desired display value
        } else {
            loader.style.display = 'none';
            // setTimeout(() => {
            // }, 500);
        }
    }

    connectionDisplayApp() {
        // This function is the first point of interaction from the user.
        // So we call the first pointer of interaction hook. In case it has anything for it to do.
        this.firstInteractionHook();

        this.toggleDisplay();
    }

    anim() {
        if (this.root.style.display === 'none') {
            this.root.classList.remove('hidden');
            this.root.style.display = 'block';
            // Trigger a reflow to apply the initial style before adding the visuallyhidden class
            this.root.offsetHeight;
            this.root.classList.remove('visuallyhidden');
        } else {
            this.root.classList.add('visuallyhidden');

            // Delay the hiding to ensure the animation is visible
            setTimeout(() => {
                this.root.style.display = 'none';
                this.root.classList.add('hidden');
            }, 600); // Adjust the delay as needed to match your transition duration
        }
    }

    runInAnim() {
        this.root.style.display = 'block';
        this.root.style.opacity = 1;
    }

    runOutAnim() {
        this.root.style.transition = 'opacity 500ms ease-out';
        this.root.style.opacity = 0;
        setTimeout(() => {
            this.root.style.display = 'none';
        }, 500);
    }

    toggleDisplay() {
        if (this.root.style.display === 'none') {
            this.anim();
        } else {
            this.anim();
        }
    }
}
